import React, { useState } from 'react';
import { Card } from '../components/ui/Card';
import { LineChart, BarChart, DoughnutChart } from '../components/charts';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Users,
  ShoppingBag,
  Package,
  ArrowRight,
  Calendar,
  Filter,
  Download
} from 'lucide-react';

export default function Overview() {
  const [dateRange, setDateRange] = useState('last30');

  const data = {
    summary: {
      totalSpend: 125000,
      spendTrend: '+12.5%',
      totalRevenue: 450000,
      revenueTrend: '+18.2%',
      roas: 3.6,
      roasTrend: '+5.7%',
      conversions: 8500,
      conversionTrend: '+15.3%'
    },
    platformPerformance: {
      googleAds: {
        spend: 75000,
        revenue: 285000,
        roas: 3.8,
        trend: '+14.2%'
      },
      metaAds: {
        spend: 50000,
        revenue: 165000,
        roas: 3.3,
        trend: '+10.8%'
      }
    },
    monthlyBudget: {
      total: 150000,
      spent: 125000,
      remaining: 25000,
      daysLeft: 8
    },
    bestProducts: [
      { name: 'Product A', revenue: 85000, orders: 1200, roas: 4.2 },
      { name: 'Product B', revenue: 65000, orders: 950, roas: 3.8 },
      { name: 'Product C', revenue: 45000, orders: 720, roas: 3.5 },
      { name: 'Product D', revenue: 35000, orders: 580, roas: 3.2 },
      { name: 'Product E', revenue: 25000, orders: 420, roas: 3.0 }
    ],
    revenueChart: {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      datasets: [
        {
          label: 'Google Revenue',
          data: [42000, 45000, 38000, 41000, 35000, 48000, 52000, 45000, 48000, 51000, 48000, 44000],
          borderColor: '#4F46E5', // indigo-600
          backgroundColor: 'rgba(79, 70, 229, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: '#4F46E5',
          pointHoverBackgroundColor: '#4F46E5',
          pointBorderColor: '#fff',
          pointHoverBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverBorderWidth: 2,
        },
        {
          label: 'Google Cost',
          data: [12000, 13500, 11000, 12500, 10500, 14000, 15500, 13000, 14500, 15000, 14000, 13000],
          borderColor: '#EF4444', // red-500
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: '#EF4444',
          pointHoverBackgroundColor: '#EF4444',
          pointBorderColor: '#fff',
          pointHoverBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverBorderWidth: 2,
        },
        {
          label: 'Meta Revenue',
          data: [28000, 31000, 25000, 27000, 24000, 32000, 35000, 30000, 33000, 34000, 32000, 29000],
          borderColor: '#10B981', // green-500
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: '#10B981',
          pointHoverBackgroundColor: '#10B981',
          pointBorderColor: '#fff',
          pointHoverBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverBorderWidth: 2,
        },
        {
          label: 'Meta Cost',
          data: [8000, 9500, 7500, 8200, 7200, 9600, 10500, 9000, 9900, 10200, 9600, 8700],
          borderColor: '#F97316', // orange-500
          backgroundColor: 'rgba(249, 115, 22, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: '#F97316',
          pointHoverBackgroundColor: '#F97316',
          pointBorderColor: '#fff',
          pointHoverBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverBorderWidth: 2,
        }
      ]
    },
    platformDistribution: {
      labels: ['Google Ads', 'Meta Ads'],
      datasets: [{
        data: [60, 40],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(14, 165, 233, 0.8)'
        ]
      }]
    },
    dailyMetrics: {
      today: {
        summary: {
          orderCount: 320,
          totalRevenue: 54500,
          avgOrderValue: 170.31,
          sessions: 15800,
          conversionRate: 2.03,
        },
        googleAds: { 
          spend: 4500, 
          revenue: 15000,
          ga4Revenue: 14200,
          cpc: 1.25,
          sessions: 4200,
          cps: 1.07
        },
        metaAds: { 
          spend: 3200, 
          revenue: 12000,
          ga4Revenue: 11500,
          cpc: 0.85,
          sessions: 4100,
          cps: 0.78
        },
        tiktokAds: {
          spend: 2800,
          revenue: 9500,
          ga4Revenue: 9000,
          cpc: 0.65,
          sessions: 4800,
          cps: 0.58
        },
        criteo: {
          spend: 1500,
          revenue: 8200,
          ga4Revenue: 7800,
          cpc: 0.45,
          sessions: 3600,
          cps: 0.42
        },
        rtbHouse: {
          spend: 1800,
          revenue: 9800,
          ga4Revenue: 9200,
          cpc: 0.52,
          sessions: 3900,
          cps: 0.46
        },
        total: { spend: 13800, revenue: 54500 }
      },
      yesterday: {
        summary: {
          orderCount: 280,
          totalRevenue: 48500,
          avgOrderValue: 173.21,
          sessions: 14200,
          conversionRate: 1.97,
        },
        googleAds: { 
          spend: 28000, 
          revenue: 98000, 
          ga4Revenue: 95000,
          cpc: 1.18,
          sessions: 25000,
          cps: 1.12
        },
        metaAds: { 
          spend: 22000, 
          revenue: 75000, 
          ga4Revenue: 72000,
          cpc: 0.92,
          sessions: 26000,
          cps: 0.85
        },
        tiktokAds: {
          spend: 22000,
          revenue: 75000,
          ga4Revenue: 72000,
          cpc: 0.92,
          sessions: 26000,
          cps: 0.85
        },
        criteo: {
          spend: 15000,
          revenue: 82000,
          ga4Revenue: 78000,
          cpc: 0.92,
          sessions: 26000,
          cps: 0.85
        },
        rtbHouse: {
          spend: 18000,
          revenue: 98000,
          ga4Revenue: 92000,
          cpc: 0.52,
          sessions: 25000,
          cps: 0.46
        },
        total: { spend: 50000, revenue: 173000 }
      },
      last7days: {
        summary: {
          totalRevenue: 173000,
          orderCount: 980,
          avgOrderValue: 176.53,
          sessions: 102000,
          conversionRate: 0.96,
        },
        googleAds: { 
          spend: 28000, 
          revenue: 98000, 
          ga4Revenue: 95000,
          cpc: 1.18,
          sessions: 25000,
          cps: 1.12
        },
        metaAds: { 
          spend: 22000, 
          revenue: 75000, 
          ga4Revenue: 72000,
          cpc: 0.92,
          sessions: 26000,
          cps: 0.85
        },
        tiktokAds: {
          spend: 22000,
          revenue: 75000,
          ga4Revenue: 72000,
          cpc: 0.92,
          sessions: 26000,
          cps: 0.85
        },
        criteo: {
          spend: 15000,
          revenue: 82000,
          ga4Revenue: 78000,
          cpc: 0.92,
          sessions: 26000,
          cps: 0.85
        },
        rtbHouse: {
          spend: 18000,
          revenue: 98000,
          ga4Revenue: 92000,
          cpc: 0.52,
          sessions: 25000,
          cps: 0.46
        },
        total: { spend: 50000, revenue: 173000 }
      },
      thisMonth: {
        summary: {
          totalRevenue: 450000,
          orderCount: 2450,
          avgOrderValue: 183.67,
          sessions: 243000,
          conversionRate: 1.01,
        },
        googleAds: { 
          spend: 75000, 
          revenue: 285000,
          ga4Revenue: 275000,
          cpc: 1.22,
          sessions: 68000,
          cps: 1.10
        },
        metaAds: { 
          spend: 50000, 
          revenue: 165000,
          ga4Revenue: 155000,
          cpc: 0.88,
          sessions: 62000,
          cps: 0.81
        },
        tiktokAds: {
          spend: 50000,
          revenue: 165000,
          ga4Revenue: 155000,
          cpc: 0.88,
          sessions: 62000,
          cps: 0.81
        },
        criteo: {
          spend: 28000,
          revenue: 98000,
          ga4Revenue: 92000,
          cpc: 0.88,
          sessions: 26000,
          cps: 0.81
        },
        rtbHouse: {
          spend: 18000,
          revenue: 98000,
          ga4Revenue: 92000,
          cpc: 0.52,
          sessions: 25000,
          cps: 0.46
        },
        total: { spend: 125000, revenue: 450000 }
      }
    },
    analytics: {
      users: 125000,
      usersTrend: '+8.5%',
      sessions: 180000,
      sessionsTrend: '+12.3%',
      bounceRate: '35.2%',
      bounceRateTrend: '-2.1%',
      avgSessionDuration: '2m 45s',
      avgSessionDurationTrend: '+15s',
      
      topSources: [
        { source: 'google / cpc', sessions: 85000, conversions: 2500, revenue: 180000 },
        { source: 'facebook / paid', sessions: 45000, conversions: 1500, revenue: 120000 },
        { source: 'direct / none', sessions: 25000, conversions: 800, revenue: 65000 },
        { source: 'google / organic', sessions: 15000, conversions: 400, revenue: 35000 }
      ],
      
      topCampaigns: [
        { name: 'Summer Sale 2024', spend: 35000, revenue: 145000, roas: 4.1 },
        { name: 'New Collection', spend: 28000, revenue: 98000, roas: 3.5 },
        { name: 'Remarketing - All', spend: 22000, revenue: 85000, roas: 3.8 }
      ]
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Revenue & Cost Chart - En üstte ve tam genişlikte */}
      <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-medium">Revenue & Cost (Last 14 Days)</h2>
          <div className="flex items-center gap-4">
            <select 
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="last30">Last 30 days</option>
              <option value="last7">Last 7 days</option>
              <option value="yesterday">Yesterday</option>
              <option value="today">Today</option>
            </select>
          </div>
        </div>
        <div className="h-[400px]"> {/* Yüksekliği artırdık */}
          <LineChart data={data.revenueChart} />
        </div>
      </Card>

      {/* Özet metrikler widget'ı */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
        {/* Revenue Widget */}
        <Card className="p-4 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <h2 className="text-base font-medium mb-4">Revenue</h2>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500">Today</p>
              <p className="text-lg font-semibold text-green-600">
                ₺{data.dailyMetrics.today.total.revenue.toLocaleString()}
              </p>
              <p className="text-xs text-green-600">+12.5% vs yesterday</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Yesterday</p>
              <p className="text-lg font-semibold text-green-600">
                ₺{data.dailyMetrics.yesterday.total.revenue.toLocaleString()}
              </p>
              <p className="text-xs text-green-600">+8.3% vs previous day</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Last 7 Days</p>
              <p className="text-lg font-semibold text-green-600">
                ₺{data.dailyMetrics.last7days.total.revenue.toLocaleString()}
              </p>
              <p className="text-xs text-green-600">+15.8% vs previous period</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">This Month</p>
              <p className="text-lg font-semibold text-green-600">
                ₺{data.dailyMetrics.thisMonth.total.revenue.toLocaleString()}
              </p>
              <p className="text-xs text-green-600">+18.2% vs previous month</p>
            </div>
          </div>
        </Card>

        {/* Transactions Widget */}
        <Card className="p-4 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <h2 className="text-base font-medium mb-4">Transactions</h2>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500">Today</p>
              <p className="text-lg font-semibold">
                {data.dailyMetrics.today.summary?.orderCount || '-'}
              </p>
              <p className="text-xs text-green-600">+10.2% vs yesterday</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Yesterday</p>
              <p className="text-lg font-semibold">
                {data.dailyMetrics.yesterday.summary?.orderCount || '-'}
              </p>
              <p className="text-xs text-green-600">+7.5% vs previous day</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Last 7 Days</p>
              <p className="text-lg font-semibold">
                {data.dailyMetrics.last7days.summary?.orderCount || '-'}
              </p>
              <p className="text-xs text-green-600">+12.8% vs previous period</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">This Month</p>
              <p className="text-lg font-semibold">
                {data.dailyMetrics.thisMonth.summary?.orderCount || '-'}
              </p>
              <p className="text-xs text-green-600">+15.3% vs previous month</p>
            </div>
          </div>
        </Card>

        {/* Avg. Order Value Widget */}
        <Card className="p-4 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <h2 className="text-base font-medium mb-4">Avg. Order Value</h2>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500">Today</p>
              <p className="text-lg font-semibold">
                ₺{data.dailyMetrics.today.summary?.avgOrderValue?.toFixed(2) || '-'}
              </p>
              <p className="text-xs text-green-600">+5.8% vs yesterday</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Yesterday</p>
              <p className="text-lg font-semibold">
                ₺{data.dailyMetrics.yesterday.summary?.avgOrderValue?.toFixed(2) || '-'}
              </p>
              <p className="text-xs text-green-600">+4.2% vs previous day</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Last 7 Days</p>
              <p className="text-lg font-semibold">
                ₺{data.dailyMetrics.last7days.summary?.avgOrderValue?.toFixed(2) || '-'}
              </p>
              <p className="text-xs text-green-600">+6.5% vs previous period</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">This Month</p>
              <p className="text-lg font-semibold">
                ₺{data.dailyMetrics.thisMonth.summary?.avgOrderValue?.toFixed(2) || '-'}
              </p>
              <p className="text-xs text-green-600">+8.5% vs previous month</p>
            </div>
          </div>
        </Card>

        {/* Sessions Widget */}
        <Card className="p-4 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <h2 className="text-base font-medium mb-4">Sessions</h2>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500">Today</p>
              <p className="text-lg font-semibold">
                {data.dailyMetrics.today.summary?.sessions?.toLocaleString() || '-'}
              </p>
              <p className="text-xs text-green-600">+11.3% vs yesterday</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Yesterday</p>
              <p className="text-lg font-semibold">
                {data.dailyMetrics.yesterday.summary?.sessions?.toLocaleString() || '-'}
              </p>
              <p className="text-xs text-green-600">+9.8% vs previous day</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Last 7 Days</p>
              <p className="text-lg font-semibold">
                {data.dailyMetrics.last7days.summary?.sessions?.toLocaleString() || '-'}
              </p>
              <p className="text-xs text-green-600">+12.3% vs previous period</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">This Month</p>
              <p className="text-lg font-semibold">
                {data.dailyMetrics.thisMonth.summary?.sessions?.toLocaleString() || '-'}
              </p>
              <p className="text-xs text-green-600">+12.3% vs previous month</p>
            </div>
          </div>
        </Card>

        {/* Conversion Rate Widget */}
        <Card className="p-4 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <h2 className="text-base font-medium mb-4">Conversion Rate</h2>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500">Today</p>
              <p className="text-lg font-semibold text-blue-600">
                %{data.dailyMetrics.today.summary?.conversionRate?.toFixed(2) || '-'}
              </p>
              <p className="text-xs text-green-600">+4.8% vs yesterday</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Yesterday</p>
              <p className="text-lg font-semibold text-blue-600">
                %{data.dailyMetrics.yesterday.summary?.conversionRate?.toFixed(2) || '-'}
              </p>
              <p className="text-xs text-green-600">+3.2% vs previous day</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Last 7 Days</p>
              <p className="text-lg font-semibold text-blue-600">
                %{data.dailyMetrics.last7days.summary?.conversionRate?.toFixed(2) || '-'}
              </p>
              <p className="text-xs text-green-600">+5.2% vs previous period</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">This Month</p>
              <p className="text-lg font-semibold text-blue-600">
                %{data.dailyMetrics.thisMonth.summary?.conversionRate?.toFixed(2) || '-'}
              </p>
              <p className="text-xs text-green-600">+5.2% vs previous month</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Platform Daily Metrics - Moved to top */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today Card */}
        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Today</h3>
            <Calendar className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {/* Google Ads */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/google_ads-512.webp" 
                  alt="Google Ads" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">Google Ads</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics.today.googleAds.spend.toLocaleString()}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics.today.googleAds.revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">GA4 Revenue</p>
                      <p className="text-xs text-orange-600">
                        ₺{data.dailyMetrics?.today?.googleAds?.ga4Revenue?.toLocaleString() || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics.today.googleAds.cpc.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics.today.googleAds.cps.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Meta Ads */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/6033716.png" 
                  alt="Meta Ads" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">Meta Ads</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics.today.metaAds.spend.toLocaleString()}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics.today.metaAds.revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">GA4 Revenue</p>
                      <p className="text-xs text-orange-600">₺{data.dailyMetrics.today.metaAds.ga4Revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics.today.metaAds.cpc.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics.today.metaAds.cps.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* TikTok Ads */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/download.png" 
                  alt="TikTok Ads" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">TikTok Ads</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics?.today?.tiktokAds?.spend?.toLocaleString() || '-'}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics?.today?.tiktokAds?.revenue?.toLocaleString() || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">GA4 Revenue</p>
                      <p className="text-xs text-orange-600">₺{data.dailyMetrics?.today?.tiktokAds?.ga4Revenue?.toLocaleString() || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics?.today?.tiktokAds?.cpc?.toFixed(2) || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics?.today?.tiktokAds?.cps?.toFixed(2) || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Criteo */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/CRTO-25f99ae9.png" 
                  alt="Criteo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">Criteo</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics?.today?.criteo?.spend?.toLocaleString() || '-'}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics?.today?.criteo?.revenue?.toLocaleString() || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">GA4 Revenue</p>
                      <p className="text-xs text-orange-600">₺{data.dailyMetrics?.today?.criteo?.ga4Revenue?.toLocaleString() || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics?.today?.criteo?.cpc?.toFixed(2) || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics?.today?.criteo?.cps?.toFixed(2) || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RTB House */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/29374191.png" 
                  alt="RTB House" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">RTB House</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics?.today?.rtbHouse?.spend?.toLocaleString() || '-'}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics?.today?.rtbHouse?.revenue?.toLocaleString() || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">GA4 Revenue</p>
                      <p className="text-xs text-orange-600">₺{data.dailyMetrics?.today?.rtbHouse?.ga4Revenue?.toLocaleString() || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics?.today?.rtbHouse?.cpc?.toFixed(2) || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics?.today?.rtbHouse?.cps?.toFixed(2) || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Total bölümü */}
            <div className="pt-3 border-t">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Spend</p>
                  <p className="text-lg font-medium">₺{data.dailyMetrics.today.total.spend.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total Revenue</p>
                  <p className="text-lg font-medium text-green-600">₺{data.dailyMetrics.today.total.revenue.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Last 7 Days Card */}
        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Last 7 Days</h3>
            <Calendar className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {/* Google Ads */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/google_ads-512.webp" 
                  alt="Google Ads" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">Google Ads</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics.last7days.googleAds.spend.toLocaleString()}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics.last7days.googleAds.revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">GA4 Revenue</p>
                      <p className="text-xs text-orange-600">₺{data.dailyMetrics.last7days.googleAds.ga4Revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics.last7days.googleAds.cpc.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics.last7days.googleAds.cps.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Meta Ads */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/6033716.png" 
                  alt="Meta Ads" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">Meta Ads</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics.last7days.metaAds.spend.toLocaleString()}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics.last7days.metaAds.revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">GA4 Revenue</p>
                      <p className="text-xs text-orange-600">₺{data.dailyMetrics.last7days.metaAds.ga4Revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics.last7days.metaAds.cpc.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics.last7days.metaAds.cps.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* TikTok Ads */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/download.png" 
                  alt="TikTok Ads" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">TikTok Ads</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics.last7days.tiktokAds.spend.toLocaleString()}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics.last7days.tiktokAds.revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics.last7days.tiktokAds.cpc.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics.last7days.tiktokAds.cps.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Criteo */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/CRTO-25f99ae9.png" 
                  alt="Criteo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">Criteo</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics.last7days.criteo.spend.toLocaleString()}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics.last7days.criteo.revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics.last7days.criteo.cpc.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics.last7days.criteo.cps.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RTB House */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/29374191.png" 
                  alt="RTB House" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">RTB House</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics.last7days.rtbHouse.spend.toLocaleString()}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics.last7days.rtbHouse.revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics.last7days.rtbHouse.cpc.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics.last7days.rtbHouse.cps.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Total bölümü */}
            <div className="pt-3 border-t">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Spend</p>
                  <p className="text-lg font-medium">₺{data.dailyMetrics.last7days.total.spend.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total Revenue</p>
                  <p className="text-lg font-medium text-green-600">₺{data.dailyMetrics.last7days.total.revenue.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* This Month Card */}
        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">This Month</h3>
            <Calendar className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {/* Google Ads */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/google_ads-512.webp" 
                  alt="Google Ads" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">Google Ads</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics.thisMonth.googleAds.spend.toLocaleString()}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics.thisMonth.googleAds.revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">GA4 Revenue</p>
                      <p className="text-xs text-orange-600">₺{data.dailyMetrics.thisMonth.googleAds.ga4Revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics.thisMonth.googleAds.cpc.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics.thisMonth.googleAds.cps.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Meta Ads */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/6033716.png" 
                  alt="Meta Ads" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">Meta Ads</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics.thisMonth.metaAds.spend.toLocaleString()}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics.thisMonth.metaAds.revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">GA4 Revenue</p>
                      <p className="text-xs text-orange-600">₺{data.dailyMetrics.thisMonth.metaAds.ga4Revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics.thisMonth.metaAds.cpc.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics.thisMonth.metaAds.cps.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* TikTok Ads */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/download.png" 
                  alt="TikTok Ads" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">TikTok Ads</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics.thisMonth.tiktokAds.spend.toLocaleString()}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics.thisMonth.tiktokAds.revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics.thisMonth.tiktokAds.cpc.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics.thisMonth.tiktokAds.cps.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Criteo */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/CRTO-25f99ae9.png" 
                  alt="Criteo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">Criteo</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics.thisMonth.criteo.spend.toLocaleString()}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics.thisMonth.criteo.revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics.thisMonth.criteo.cpc.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics.thisMonth.criteo.cps.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RTB House */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/29374191.png" 
                  alt="RTB House" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">RTB House</p>
                  <p className="text-sm font-medium">₺{data.dailyMetrics.thisMonth.rtbHouse.spend.toLocaleString()}</p>
                </div>
                <div className="flex justify-between mt-1">
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs text-gray-400">Revenue</p>
                      <p className="text-xs text-green-600">₺{data.dailyMetrics.thisMonth.rtbHouse.revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPC</p>
                      <p className="text-xs text-blue-600">₺{data.dailyMetrics.thisMonth.rtbHouse.cpc.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">CPS</p>
                      <p className="text-xs text-purple-600">₺{data.dailyMetrics.thisMonth.rtbHouse.cps.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Total bölümü */}
            <div className="pt-3 border-t">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Spend</p>
                  <p className="text-lg font-medium">₺{data.dailyMetrics.thisMonth.total.spend.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total Revenue</p>
                  <p className="text-lg font-medium text-green-600">₺{data.dailyMetrics.thisMonth.total.revenue.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Spend</p>
              <p className="text-2xl font-bold mt-2">₺{data.summary.totalSpend.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-2xl font-bold mt-2">₺{data.summary.totalRevenue.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">ROAS</p>
              <p className="text-2xl font-bold mt-2">{data.summary.roas.toFixed(1)}x</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-xl">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Conversions</p>
              <p className="text-2xl font-bold mt-2">{data.summary.conversions.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-xl">
              <ShoppingBag className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Platform Distribution */}
        <div>
          <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
            <h2 className="text-lg font-medium mb-6">Ad Spend Distribution</h2>
            <div className="h-[300px]">
              <DoughnutChart data={data.platformDistribution} />
            </div>
          </Card>
        </div>
      </div>

      {/* Google Analytics Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Users</p>
              <p className="text-2xl font-bold mt-1">{data.analytics.users.toLocaleString()}</p>
            </div>
            <img src="/Google-Analytics-Logo (1).png" alt="GA" className="w-8 h-8" />
          </div>
          <p className="text-sm text-green-600">{data.analytics.usersTrend} vs prev. period</p>
        </Card>

        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Sessions</p>
              <p className="text-2xl font-bold mt-1">{data.analytics.sessions.toLocaleString()}</p>
            </div>
            <img src="/Google-Analytics-Logo (1).png" alt="GA" className="w-8 h-8" />
          </div>
          <p className="text-sm text-green-600">{data.analytics.sessionsTrend} vs prev. period</p>
        </Card>

        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Bounce Rate</p>
              <p className="text-2xl font-bold mt-1">{data.analytics.bounceRate}</p>
            </div>
            <img src="/Google-Analytics-Logo (1).png" alt="GA" className="w-8 h-8" />
          </div>
          <p className="text-sm text-green-600">{data.analytics.bounceRateTrend} vs prev. period</p>
        </Card>

        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Avg. Session Duration</p>
              <p className="text-2xl font-bold mt-1">{data.analytics.avgSessionDuration}</p>
            </div>
            <img src="/Google-Analytics-Logo (1).png" alt="GA" className="w-8 h-8" />
          </div>
          <p className="text-sm text-green-600">{data.analytics.avgSessionDurationTrend} vs prev. period</p>
        </Card>
      </div>

      {/* Platform Cards & Monthly Budget */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Google Ads */}
        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Google Ads</h3>
            <div className="p-2 bg-blue-50 rounded-lg">
              <img src="/google-ads-icon.png" alt="Google Ads" className="w-6 h-6" />
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-gray-500">Spend</p>
              <p className="font-medium">₺{data.platformPerformance.googleAds.spend.toLocaleString()}</p>
            </div>
            <div className="flex justify-between items-center">
              <p className="text-gray-500">Revenue</p>
              <p className="font-medium">₺{data.platformPerformance.googleAds.revenue.toLocaleString()}</p>
            </div>
            <div className="flex justify-between items-center">
              <p className="text-gray-500">ROAS</p>
              <p className="font-medium">{data.platformPerformance.googleAds.roas.toFixed(1)}x</p>
            </div>
            <div className="flex justify-between items-center text-green-600">
              <p className="text-sm">vs prev. period</p>
              <p className="text-sm">{data.platformPerformance.googleAds.trend}</p>
            </div>
          </div>
        </Card>

        {/* Meta Ads */}
        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Meta Ads</h3>
            <div className="p-2 bg-blue-50 rounded-lg">
              <img src="/meta-ads-icon.png" alt="Meta Ads" className="w-6 h-6" />
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-gray-500">Spend</p>
              <p className="font-medium">₺{data.platformPerformance.metaAds.spend.toLocaleString()}</p>
            </div>
            <div className="flex justify-between items-center">
              <p className="text-gray-500">Revenue</p>
              <p className="font-medium">₺{data.platformPerformance.metaAds.revenue.toLocaleString()}</p>
            </div>
            <div className="flex justify-between items-center">
              <p className="text-gray-500">ROAS</p>
              <p className="font-medium">{data.platformPerformance.metaAds.roas.toFixed(1)}x</p>
            </div>
            <div className="flex justify-between items-center text-green-600">
              <p className="text-sm">vs prev. period</p>
              <p className="text-sm">{data.platformPerformance.metaAds.trend}</p>
            </div>
          </div>
        </Card>

        {/* Monthly Budget */}
        <Card className="p-6 rounded-2xl shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Monthly Budget</h3>
            <div className="p-2 bg-green-50 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-2">
              <p className="text-gray-500">Budget Spent</p>
              <p className="font-medium">₺{data.monthlyBudget.spent.toLocaleString()}</p>
            </div>
            <div className="h-2 bg-gray-100 rounded-full">
              <div 
                className="h-full bg-green-500 rounded-full"
                style={{ width: `${(data.monthlyBudget.spent / data.monthlyBudget.total) * 100}%` }}
              ></div>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <p className="text-gray-500">Remaining</p>
            <p className="font-medium">₺{data.monthlyBudget.remaining.toLocaleString()}</p>
          </div>
          <div className="flex justify-between items-center">
            <p className="text-gray-500">Days Left</p>
            <p className="font-medium">{data.monthlyBudget.daysLeft} days</p>
          </div>
          <div className="flex justify-between items-center text-blue-600">
            <p className="text-sm">Daily Budget</p>
            <p className="text-sm">₺{Math.round(data.monthlyBudget.remaining / data.monthlyBudget.daysLeft).toLocaleString()}</p>
          </div>
        </Card>
      </div>

      {/* Best Performing Products */}
      <Card className="rounded-2xl overflow-hidden shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium">Best Performing Products</h2>
            <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
              View All Products
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left py-3 px-4">Product</th>
                <th className="text-right py-3 px-4">Revenue</th>
                <th className="text-right py-3 px-4">Orders</th>
                <th className="text-right py-3 px-4">ROAS</th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {data.bestProducts.map((product, index) => (
                <tr key={index} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gray-100"></div>
                      <p className="font-medium">{product.name}</p>
                    </div>
                  </td>
                  <td className="text-right py-3 px-4">₺{product.revenue.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">{product.orders.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">{product.roas.toFixed(1)}x</td>
                  <td className="text-right py-3 px-4">
                    <button className="text-gray-400 hover:text-gray-600">
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Top Sources Table */}
      <Card className="rounded-2xl overflow-hidden shadow-[0_3px_10px_rgba(0,0,0,0.15)]">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium">Top Sources</h2>
            <img src="/Google-Analytics-Logo (1).png" alt="GA" className="w-6 h-6" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left py-3 px-4">Source / Medium</th>
                <th className="text-right py-3 px-4">Sessions</th>
                <th className="text-right py-3 px-4">Conversions</th>
                <th className="text-right py-3 px-4">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.analytics.topSources.map((source, index) => (
                <tr key={index} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium">{source.source}</td>
                  <td className="text-right py-3 px-4">{source.sessions.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">{source.conversions.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">₺{source.revenue.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
} 